import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import {
  Rows,
  Columns,
  Box,
  Square,
  Minus,
  Type,
  Image,
  MousePointer,
  Star,
  Play,
  ListChecks,
  ChevronDown,
  Link,
  LayoutTemplate,
  Search,
  X,
  Layers,
} from 'lucide-react';
import { getComponentDefinition } from '../componentDefinitions';

const ICON_MAP = {
  Rows,
  Columns,
  Box,
  Square,
  Minus,
  Type,
  Image,
  MousePointer,
  Star,
  Play,
  Video: Play,
  Link,
  ListChecks,
  ChevronDown,
  LayoutTemplate,
  Layers,
};

const CATEGORY_ORDER = ['structure', 'layout', 'content'];
const CATEGORY_LABELS = { structure: 'Structure', layout: 'Layout', content: 'Content' };

const ANIM_DURATION = 200;

const ComponentPicker = ({ onSelectComponent, onClose, availableComponents, showSectionItem = false }) => {
  const [search, setSearch] = useState('');
  const [highlightIndex, setHighlightIndex] = useState(0);
  const [visible, setVisible] = useState(false);
  const [closing, setClosing] = useState(false);
  const searchRef = useRef(null);
  const itemRefs = useRef({});

  // Trigger enter animation on mount
  useEffect(() => {
    requestAnimationFrame(() => setVisible(true));
  }, []);

  // Animated close handler
  const handleClose = useCallback(() => {
    if (closing) return;
    setClosing(true);
    setVisible(false);
    setTimeout(onClose, ANIM_DURATION);
  }, [onClose, closing]);

  // Build enriched component list from definitions
  const allItems = useMemo(() => {
    const sectionItem = showSectionItem ? [{ type: '_section', label: 'Section', description: 'New blank section', category: 'structure', icon: 'LayoutTemplate' }] : [];

    // Quick layout items
    const quickLayoutItems = [
      ...(showSectionItem ? [{ type: '_quickLayout_centeredContent', label: 'Centered Content', description: 'Section with centered container and block', category: 'structure', icon: 'Layers', isQuickLayout: true }] : []),
      { type: '_quickLayout_2col', label: '2 Column', description: 'Row with 2 equal columns', category: 'layout', icon: 'Columns', isQuickLayout: true },
      { type: '_quickLayout_3col', label: '3 Column', description: 'Row with 3 equal columns', category: 'layout', icon: 'Columns', isQuickLayout: true },
    ];

    const componentItems = availableComponents.map((type) => {
      const def = getComponentDefinition(type);
      return {
        type,
        label: def?.label || type,
        description: def?.description || '',
        category: def?.category || 'content',
        icon: def?.icon || 'Box',
      };
    });

    return [...sectionItem, ...quickLayoutItems, ...componentItems];
  }, [availableComponents, showSectionItem]);

  // Filter by search
  const filtered = useMemo(() => {
    if (!search.trim()) return allItems;
    const q = search.toLowerCase();
    return allItems.filter(
      (item) =>
        item.label.toLowerCase().includes(q) ||
        item.description.toLowerCase().includes(q) ||
        item.type.toLowerCase().includes(q)
    );
  }, [allItems, search]);

  // Group by category
  const grouped = useMemo(() => {
    const groups = {};
    for (const cat of CATEGORY_ORDER) {
      const items = filtered.filter((item) => item.category === cat);
      if (items.length > 0) groups[cat] = items;
    }
    const coveredTypes = new Set(Object.values(groups).flat().map((i) => i.type));
    const remaining = filtered.filter((i) => !coveredTypes.has(i.type));
    if (remaining.length > 0) groups['other'] = remaining;
    return groups;
  }, [filtered]);

  // Flat list for keyboard navigation
  const flatList = useMemo(() => {
    const list = [];
    for (const cat of Object.keys(grouped)) {
      for (const item of grouped[cat]) {
        list.push(item);
      }
    }
    return list;
  }, [grouped]);

  // Reset highlight when filter changes
  useEffect(() => {
    setHighlightIndex(0);
  }, [search]);

  // Scroll highlighted item into view
  useEffect(() => {
    const el = itemRefs.current[highlightIndex];
    if (el) {
      el.scrollIntoView({ block: 'nearest' });
    }
  }, [highlightIndex]);

  // Auto-focus search
  useEffect(() => {
    searchRef.current?.focus();
  }, []);

  const handleSelect = useCallback(
    (type) => {
      onSelectComponent(type);
    },
    [onSelectComponent]
  );

  // Grid columns for keyboard nav
  const COLS = 4;

  const handleKeyDown = useCallback(
    (e) => {
      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          setHighlightIndex((prev) => Math.min(prev + COLS, flatList.length - 1));
          break;
        case 'ArrowUp':
          e.preventDefault();
          setHighlightIndex((prev) => Math.max(prev - COLS, 0));
          break;
        case 'ArrowRight':
          e.preventDefault();
          setHighlightIndex((prev) => Math.min(prev + 1, flatList.length - 1));
          break;
        case 'ArrowLeft':
          e.preventDefault();
          setHighlightIndex((prev) => Math.max(prev - 1, 0));
          break;
        case 'Enter':
          e.preventDefault();
          if (flatList[highlightIndex]) {
            handleSelect(flatList[highlightIndex].type);
          }
          break;
        case 'Escape':
          e.preventDefault();
          handleClose();
          break;
        default:
          break;
      }
    },
    [flatList, highlightIndex, handleSelect, handleClose]
  );

  let itemIndex = -1;

  return (
    <div
      className="fixed inset-0 flex items-end justify-center z-50 transition-colors"
      style={{
        backgroundColor: visible ? 'rgba(0,0,0,0.15)' : 'rgba(0,0,0,0)',
        transitionDuration: `${ANIM_DURATION}ms`,
      }}
      onClick={handleClose}
      onKeyDown={handleKeyDown}
    >
      <div
        className="w-full max-w-2xl bg-white rounded-t-xl shadow-2xl overflow-hidden transition-transform"
        style={{
          transform: visible ? 'translateY(0)' : 'translateY(100%)',
          transitionDuration: `${ANIM_DURATION}ms`,
          transitionTimingFunction: visible ? 'cubic-bezier(0.16,1,0.3,1)' : 'cubic-bezier(0.4,0,1,1)',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header with search */}
        <div className="flex items-center gap-3 px-4 pt-3 pb-2">
          <div className="relative flex-1">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400 pointer-events-none" />
            <input
              ref={searchRef}
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search components..."
              className="w-full pl-8 pr-7 py-1.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-blue-400 focus:ring-1 focus:ring-blue-400"
            />
            {search && (
              <button
                onClick={() => setSearch('')}
                className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            )}
          </div>
          <button
            onClick={handleClose}
            className="p-1 text-gray-400 hover:text-gray-600 rounded-md hover:bg-gray-100"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Component grid */}
        <div className="px-4 pb-4 max-h-[280px] overflow-y-auto">
          {flatList.length === 0 ? (
            <div className="py-8 text-center text-sm text-gray-400">
              No components found
            </div>
          ) : (
            Object.entries(grouped).map(([category, items]) => (
              <div key={category}>
                <div className="text-[10px] text-gray-500 uppercase tracking-wide font-medium px-1 pt-2 pb-1.5">
                  {CATEGORY_LABELS[category] || category}
                </div>
                <div className="grid grid-cols-4 gap-1.5">
                  {items.map((item) => {
                    itemIndex++;
                    const idx = itemIndex;
                    const isHighlighted = idx === highlightIndex;
                    const IconComponent = ICON_MAP[item.icon] || Box;

                    return (
                      <button
                        key={item.type}
                        ref={(el) => (itemRefs.current[idx] = el)}
                        onClick={() => handleSelect(item.type)}
                        onMouseEnter={() => setHighlightIndex(idx)}
                        className={`flex flex-col items-center gap-1 px-2 py-2.5 rounded-lg text-center transition-colors ${
                          isHighlighted
                            ? 'bg-blue-50 text-blue-700 ring-1 ring-blue-200'
                            : 'hover:bg-gray-50 text-gray-700'
                        }`}
                      >
                        <IconComponent
                          className={`w-5 h-5 ${
                            isHighlighted ? 'text-blue-600' : 'text-gray-400'
                          }`}
                        />
                        <span className="text-xs font-medium leading-tight">
                          {item.label}
                        </span>
                        {item.isQuickLayout && (
                          <span className={`text-[9px] leading-none ${isHighlighted ? 'text-blue-400' : 'text-gray-400'}`}>Layout</span>
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

export default ComponentPicker;
